"use client";

import { Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import { outcomes } from "@/lib/data/homepage";
import { CrisisTimeline } from "./visuals";

/**
 * Outcomes — Asymmetric before/after layout. Left: 4 stat cards in
 * bento. Right: Crisis timeline visualization showing the value
 * Perception X2 brings.
 */
export function Outcomes() {
  return (
    <section id="outcomes" className="relative section-pad">
      <Container>
        <Reveal className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass eyebrow-tiny text-[var(--color-emerald)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-emerald)]" />
            Outcomes
          </span>
          <h2>
            Results That Transform Organizations
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
          {/* LEFT — 4 stat cards in 2x2 with varying sizes */}
          <div className="grid sm:grid-cols-2 gap-4">
            {outcomes.map((o, i) => (
              <Reveal key={o.title} delay={i * 80}>
                <div
                  className={`rounded-3xl glass p-6 h-full relative overflow-hidden card-glow ${i === 0 ? "sm:col-span-2" : ""}`}
                  style={{ ["--glow" as string]: o.accent }}
                >
                  <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full blur-3xl opacity-20" style={{ background: o.accent }} />
                  <div className="relative">
                    <div
                      className={`font-mono-num leading-[0.9] tracking-tight ${i === 0 ? "display-stat-lg" : "display-stat-md"}`}
                      style={{ color: o.accent }}
                    >
                      {o.stat}
                    </div>
                    <h3 className={`font-bold leading-tight mt-3 ${i === 0 ? "text-xl" : "text-base"}`}>
                      {o.title}
                    </h3>
                    <p className={`mt-2 text-[rgb(var(--fg-muted))] leading-relaxed ${i === 0 ? "text-body-sm" : "text-caption"}`}>
                      {o.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* RIGHT — Crisis timeline visualization */}
          <Reveal delay={120}>
            <div className="rounded-3xl glass p-8 h-full relative overflow-hidden card-glow" style={{ ["--glow" as string]: "var(--color-crimson)" }}>
              <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[var(--color-crimson)] opacity-15 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="eyebrow-tiny text-[var(--color-crimson)] mb-2">
                  Crisis Response Curve
                </div>
                <h3 className="text-2xl font-bold leading-tight mb-1">
                  From collapse to recovery — in hours.
                </h3>
                <p className="text-sm text-[rgb(var(--fg-muted))] mb-8">
                  Sentiment trajectory: crisis detection through autonomous response to recovered perception.
                </p>
                <CrisisTimeline />
                <div className="mt-8 grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-2xl font-bold font-mono-num" style={{ color: "var(--color-crimson)" }}>
                      <Counter to={2} suffix="h" />
                    </div>
                    <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1 font-semibold">
                      Detection
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold font-mono-num" style={{ color: "var(--color-amber)" }}>
                      <Counter to={4} suffix="h" />
                    </div>
                    <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1 font-semibold">
                      Response
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold font-mono-num" style={{ color: "var(--color-emerald)" }}>
                      <Counter to={72} suffix="h" />
                    </div>
                    <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1 font-semibold">
                      Recovery
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}
